import React, { forwardRef, useMemo } from 'react';

import { getPrintedTextColor, getFoilCssProperties } from '../../util/foilColors';
import { ptToDpi, PT_TO_DPI_RATIO } from '../../../../util/font/pointPixelConversion';

import { CurvedTextElementView } from '../../types/ElementView';
import { getFontAttributesFromTextElementView } from '../../util/elementViewHelpers';

type CurvedTextProps = {
  textElement: CurvedTextElementView;
  isActive?: boolean;
  foilColor?: string;
};

const getTextAlignment = (horizontalAlignment: string | undefined) => {
  switch (horizontalAlignment) {
    case 'RIGHT':
      return {
        startOffset: '100%',
        textAnchor: 'end',
      };
    case 'LEFT':
      return {
        startOffset: '0%',
        textAnchor: 'start',
      };
    case 'CENTER':
    default:
      return {
        startOffset: '50%',
        textAnchor: 'middle',
      };
  }
};

/**
 * Renders a curved text element following the SVG path provided by the backend.
 */
export const CurvedText = forwardRef<SVGTextElement, CurvedTextProps>(
  ({ textElement, foilColor, isActive }, ref) => {
    const {
      body,
      id,
      uuid,
      height,
      width,
      svg_curve_path: svgCurvePath,
      printing_technique: printingTechnique,
    } = textElement;

    const elementId = uuid || id;

    const {
      horizontalAlignment,
      fontColor,
      fontSize,
      fontFamily,
      fontStyle,
      fontWeight,
      letterSpacing,
    } = getFontAttributesFromTextElementView(textElement);

    const displayedTextColor = getPrintedTextColor(fontColor, printingTechnique, foilColor);

    const textStyles: React.CSSProperties = useMemo(() => {
      // * Foil specific CSS effect
      const foilProperties = !isActive
        ? getFoilCssProperties(foilColor, printingTechnique)
        : ({} as React.CSSProperties);

      return {
        fontVariantLigatures: 'none',
        fontKerning: 'none',
        transform: `scale(${PT_TO_DPI_RATIO})`,
        ...foilProperties,
      };
    }, [foilColor, printingTechnique, isActive]);

    const curveId = `curve-${elementId}`;

    // The SVG needs to be larger than the actual element to avoid cutting off text (since the path runs through the middle of the text line).
    // The BE cuts off text that extends more than 2 * fontSize on any side, so let's do the same thing here.
    const svgOffsetPt = -fontSize;
    const svgWidthPt = width + fontSize * 2;
    const svgHeightPt = height + fontSize * 2;
    const svgOffsetDPI = ptToDpi(svgOffsetPt);
    const svgWidthDPI = ptToDpi(svgWidthPt);
    const svgHeightDPI = ptToDpi(svgHeightPt);

    const viewBox = useMemo(() => {
      return `${svgOffsetDPI} ${svgOffsetDPI} ${svgWidthDPI} ${svgHeightDPI}`;
    }, [svgOffsetDPI, svgWidthDPI, svgHeightDPI]);

    const svgStyle: React.CSSProperties = useMemo(
      () => ({
        position: 'absolute',
        top: `${svgOffsetPt}pt`,
        left: `${svgOffsetPt}pt`,
      }),
      [svgOffsetPt]
    );

    return (
      <svg
        width={`${svgWidthPt}pt`}
        height={`${svgHeightPt}pt`}
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        xmlSpace="preserve"
        id={`curved-text-svg--${elementId}`}
        style={svgStyle}
      >
        <defs>
          <path id={curveId} d={svgCurvePath} fill="none" />
          <clipPath id="clipPathForText">
            <rect x={svgOffsetDPI} y={svgOffsetDPI} width={svgWidthDPI} height={svgHeightDPI} />
          </clipPath>
        </defs>
        <g>
          {/* Helpful when debugging the curve: */}
          {/* <path
            d={svgCurvePath}
            fillOpacity="0"
            stroke="magenta"
            transform={`scale(${ptToPx})`}
            display={isActive ? 'auto' : 'none'}
          /> */}
          <text
            ref={ref}
            fontSize={`${fontSize}`}
            fill={`#${displayedTextColor}`}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            fontStyle={fontStyle}
            letterSpacing={`${letterSpacing}em`}
            style={textStyles}
            clipPath="url(#clipPathForText)"
          >
            <textPath
              xlinkHref={`#${curveId}`}
              letterSpacing={`${letterSpacing}em`}
              dominantBaseline="central"
              {...getTextAlignment(horizontalAlignment)}
            >
              {body}
            </textPath>
          </text>
        </g>
      </svg>
    );
  }
);
